'use client';

/**
 * ShareButtons Component
 * 
 * A reusable component for sharing content on social media platforms.
 * 
 * @param {Object} props
 * @param {string} props.url - The URL to share (defaults to current page URL)
 * @param {string} props.title - The title of the content to share
 * @param {string} props.description - Optional description for the share
 * @param {string} props.variant - Display variant: 'horizontal' (default) or 'vertical'
 * @param {string} props.size - Icon size: 'sm', 'md' (default), or 'lg'
 * @param {string} props.className - Additional CSS classes
 */
export function ShareButtons({
    url,
    title,
    description = '',
    variant = 'horizontal',
    size = 'md',
    className = '',
}) {
    const shareLinks = {
        facebook: {
            name: 'Facebook',
            icon: (props) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path
                        fillRule="evenodd"
                        d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                        clipRule="evenodd"
                    />
                </svg>
            ),
        },
        twitter: {
            name: 'X (Twitter)',
            icon: (props) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
                </svg>
            ),
        },
        linkedin: {
            name: 'LinkedIn',
            icon: (props) => (
                <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
            ),
        },
        email: {
            name: 'Email',
            icon: (props) => (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            ),
        },
    };

    const sizeClasses = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
    };

    const containerClasses = {
        horizontal: 'flex gap-x-3',
        vertical: 'flex flex-col gap-y-3',
    };

    const iconSize = sizeClasses[size] || sizeClasses.md;
    const containerClass = containerClasses[variant] || containerClasses.horizontal;

    const handleShare = (platform) => {
        const encodedUrl = encodeURIComponent(url || window.location.href);
        const encodedTitle = encodeURIComponent(title || '');
        const encodedDescription = encodeURIComponent(description || '');
        const shareUrl = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            email: `mailto:?subject=${encodedTitle}&body=${encodedDescription ? encodedDescription + '%0A%0A' : ''}${encodedUrl}`,
        }[platform];
        // Open share window
        const width = 600;
        const height = 400;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        if (platform === 'email') {
            window.location.assign(shareUrl);
        } else {
            window.open(
                shareUrl,
                'share',
                `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0`
            );
        }
    };

    return (
        <div className={className}>
            <div className={`${containerClass} items-center`}>
                {Object.entries(shareLinks).map(([key, item]) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={key}
                            onClick={() => handleShare(key)}
                            className="text-gray-500 hover:text-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded"
                            aria-label={`Share on ${item.name}`}
                            title={`Share on ${item.name}`}
                        >
                            <Icon
                                aria-hidden="true"
                                className={iconSize}
                            />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
